import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getEmbassy } from "@/data/embassies";
import { EmbassyCountryPageEn, embassyFaqsEn, turnaroundEn } from "@/components/EmbassyCountryPageEn";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema } from "@/lib/aeo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/embassy/$country")({
  loader: ({ params }) => {
    const c = getEmbassy(params.country);
    if (!c) throw notFound();
    return { slug: params.country };
  },
  head: ({ params }) => {
    const c = getEmbassy(params.country);
    if (!c) return { meta: [{ title: "Country unavailable" }, { name: "robots", content: "noindex" }] };
    const url = `${SITE}/en/embassy/${c.slug}`;
    const ta = turnaroundEn(c.turnaround);
    const title = `${c.nameEn} Embassy Legalization Bangkok — Translation + MFA + Consular | NYC`;
    const desc = `End-to-end legalization for the ${c.nameEn} embassy in Bangkok: certified translation, Thai MFA legalization and consular filing in about ${ta}. Talk to us on LINE.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/embassy/$country", "en") },
        { name: "description", content: ctaDesc(desc, "/en/embassy/$country", "en") },
        { property: "og:title", content: ctaTitle(title, "/en/embassy/$country", "en") },
        { property: "og:description", content: ctaDesc(desc, "/en/embassy/$country", "en") },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "th", href: `${SITE}/embassy/${c.slug}` },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [consolidateGraph([
        speakableSchema([".aeo-speakable", "h1", "h2"]),
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${url}#service`,
            name: `${c.nameEn} Embassy Legalization in Bangkok`,
            serviceType: "Consular Legalization",
            inLanguage: "en",
            areaServed: [
              { "@type": "Country", name: "Thailand" },
              { "@type": "Country", name: c.nameEn },
            ],
            provider: {
              "@type": "LegalService",
              "@id": `${SITE}/#org`,
              name: "NYC Legal & Notary Service Co., Ltd.",
              url: SITE,
              telephone: "+66832494999",
            },
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            inLanguage: "en",
            mainEntity: embassyFaqsEn(c).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${url}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
              { "@type": "ListItem", position: 2, name: "Embassy legalization", item: `${SITE}/en/embassy` },
              { "@type": "ListItem", position: 3, name: `${c.nameEn} embassy`, item: url },
            ],
          }),
        },
      ])],
    };
  },
  component: RouteComp,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Country not found</h1>
      <Link to="/en/embassy" className="mt-4 inline-block text-gold underline">
        Back to all embassies
      </Link>
    </main>
  ),
});

function RouteComp() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const c = getEmbassy(slug)!;
  return <EmbassyCountryPageEn c={c} />;
}
